import {
  Checkbox,
  List,
  ListItem,
  SectionMessage,
  Text,
} from "@certn/cascadia-react";
import { Stack } from "@certn/cascadia";
import { useTranslation } from "react-i18next";

interface ConsumerReportCertificationSectionProps {
  acknowledged: boolean;
  onAcknowledgedChange: (acknowledged: boolean) => void;
}

export function ConsumerReportCertificationSection({
  acknowledged,
  onAcknowledgedChange,
}: ConsumerReportCertificationSectionProps) {
  const { t } = useTranslation();

  return (
    <SectionMessage
      type="default"
      icon="info"
      sectionTitle={t("Consumer Report Certifications")}
      testID="consumer-report-certification"
      fontSize="md"
    >
      <Stack gap="space-4">
        <Text style="global/body-secondary">
          {t(
            "The following certifications apply to your request for a consumer report and must be acknowledged prior to submission."
          )}
        </Text>
        <List type="ordered">
          <ListItem>
            <Text style="global/body-secondary">
              {t(
                "This report is being requested for employment purposes only."
              )}
            </Text>
          </ListItem>
          <ListItem>
            <Text style="global/body-secondary">
              {t(
                "Prior to this consumer report request being submitted, a disclosure will be provided to the consumer in accordance with requirements set forth in the Fair Credit Reporting Act (FCRA) and the consumer will authorize in writing that this report may be procured."
              )}
            </Text>
          </ListItem>
          <ListItem>
            <Text style="global/body-secondary">
              {t(
                "Information contained in the background check will not be used in violation of any federal or state equal opportunity law or regulation."
              )}
            </Text>
          </ListItem>
          <ListItem>
            <Text style="global/body-secondary">
              {t(
                "If I am requesting an investigative consumer report on this consumer, that upon the written request the consumer made within a reasonable period of time after the disclosures required above, I will make a complete disclosure of the nature and scope of the investigation. This will be made in a written statement that is mailed, or otherwise delivered, to the consumer no later than five days after the date on which the request was received from the consumer or the report was first requested, whichever is later in time."
              )}
            </Text>
          </ListItem>
          <ListItem>
            <Text style="global/body-secondary">
              {t(
                "If my jurisdiction does not allow a background check to be procured until after a conditional offer has been made, that I have made this conditional offer before requesting this report."
              )}
            </Text>
          </ListItem>
          <ListItem>
            <Text style="global/body-secondary">
              {t(
                'I will comply with New York City Law. I understand that the New York City Human Rights Law ("NYCHRL") imposes various legal requirements on covered employers and businesses. I will comply with all applicable requirements under NYCHRL. Among other things, I shall: (A) Not obtain or evaluate criminal history information about an applicant or employee until after a conditional offer of employment, promotion, or transfer has been extended to him or her; (B) Not inquire about or consider criminal history that is prohibited from being considered under NYCHRL.'
              )}
            </Text>
          </ListItem>
          <ListItem>
            <Text style="global/body-secondary">
              {t(
                "I certify that before taking any adverse employment action based in whole or in part on information contained in a consumer report, I will comply with the adverse action requirements of the Fair Credit Reporting Act (FCRA)."
              )}
            </Text>
          </ListItem>
        </List>
        <Checkbox
          isSelected={acknowledged}
          onChange={onAcknowledgedChange}
          testID="consumer-report-certification-checkbox"
        >
          {t(
            "By checking this box, I certify that the statements above are true and accurate and that I will comply with all applicable laws regarding the procurement and use of consumer reports."
          )}
        </Checkbox>
      </Stack>
    </SectionMessage>
  );
}
